import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { API, INTERESTS, formatApiError } from "../../lib/contact";

const EMPTY = { name: "", phone: "", city: "", interest: "Még nem döntöttem" };
const field = "w-full border-2 border-navy bg-ivory px-4 py-4 text-lg font-normal text-navy rounded-none transition-colors focus:outline-none focus:border-coral";

export const InquiryForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error("Kérjük, adja meg a nevét.");
    if ((form.phone.match(/\d/g) || []).length < 6) return toast.error("Kérjük, adjon meg érvényes telefonszámot.");
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setDone(true);
      setForm(EMPTY);
      toast.success("Köszönjük! Hamarosan visszahívjuk.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div data-testid="inquiry-success" className="mt-10 border-2 border-sage bg-ivory p-8 flex gap-5 items-start">
        <CheckCircle2 className="h-10 w-10 text-sage shrink-0" />
        <div>
          <p className="text-2xl font-semibold">Köszönjük bizalmát!</p>
          <p className="text-lg mt-2">Megkeresését megkaptuk. Hamarosan visszahívjuk egy kötetlen, barátságos beszélgetésre.</p>
          <button type="button" data-testid="inquiry-new-button" onClick={() => setDone(false)} className="mt-4 underline font-semibold">
            Új megkeresés küldése
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="inquiry-form" aria-label="Bizalmas Megkeresés" className="mt-10 grid md:grid-cols-2 gap-6 text-lg">
      <label className="grid gap-2 font-semibold">Név
        <input data-testid="inquiry-name-input" className={field} value={form.name} onChange={set("name")} autoComplete="name" required />
      </label>
      <label className="grid gap-2 font-semibold">Telefonszám
        <input data-testid="inquiry-phone-input" type="tel" className={`${field} tab`} value={form.phone} onChange={set("phone")} autoComplete="tel" required />
      </label>
      <label className="grid gap-2 font-semibold">Város/Környék
        <input data-testid="inquiry-city-input" className={field} value={form.city} onChange={set("city")} autoComplete="address-level2" />
      </label>
      <div className="grid gap-2 font-semibold">
        <span id="interest-label">Megjegyzés</span>
        <Select value={form.interest} onValueChange={(v) => setForm({ ...form, interest: v })}>
          <SelectTrigger aria-labelledby="interest-label" data-testid="inquiry-interest-select" className={`${field} h-auto`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-none border-2 border-navy bg-ivory text-navy">
            {INTERESTS.map((o, i) => (
              <SelectItem key={o} value={o} data-testid={`inquiry-interest-option-${i + 1}`} className="rounded-none py-3 text-lg focus:bg-mist">
                {o}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <button
        type="submit"
        disabled={sending}
        data-testid="inquiry-submit-button"
        className="md:col-span-2 bg-coral text-coral-ink px-8 py-5 text-xl font-semibold inline-flex justify-center items-center gap-3 transition-colors hover:bg-coral-deep disabled:opacity-70"
      >
        {sending && <Loader2 className="h-6 w-6 animate-spin" />} Bizalmas Visszahívást Kérek
      </button>
      <p className="md:col-span-2 text-base font-normal text-navy/75">
        Adatait bizalmasan kezeljük, és kizárólag a visszahíváshoz használjuk fel.
      </p>
    </form>
  );
};
